import type { CurveType } from 'recharts/types/shape/Curve'
import type { AxisInterval } from 'recharts/types/util/types'

/**
 * Standard curve types used across the application.
 * These are typed constants for Recharts curve interpolation.
 */
export const CURVE_TYPES = {
  BASIS: 'basis' as CurveType,
  BASIS_CLOSED: 'basisClosed' as CurveType,
  BASIS_OPEN: 'basisOpen' as CurveType,
  BUMP_X: 'bumpX' as CurveType,
  BUMP_Y: 'bumpY' as CurveType,
  BUMP: 'bump' as CurveType,
  LINEAR: 'linear' as CurveType,
  LINEAR_CLOSED: 'linearClosed' as CurveType,
  NATURAL: 'natural' as CurveType,
  MONOTONE_X: 'monotoneX' as CurveType,
  MONOTONE_Y: 'monotoneY' as CurveType,
  MONOTONE: 'monotone' as CurveType,
  STEP: 'step' as CurveType,
  STEP_BEFORE: 'stepBefore' as CurveType,
  STEP_AFTER: 'stepAfter' as CurveType,
} as const

/**
 * Standard interval types for axis tick display.
 * These are typed constants for Recharts axis interval configuration.
 */
export const AXIS_INTERVALS = {
  PRESERVE_START: 'preserveStart' as AxisInterval,
  PRESERVE_END: 'preserveEnd' as AxisInterval,
  PRESERVE_START_END: 'preserveStartEnd' as AxisInterval,
  EQUIDISTANT_PRESERVE_START: 'equidistantPreserveStart' as AxisInterval,
} as const

// Data types
export interface DataPoint {
  label?: string
  x: number | string | null
  y: number | string | null
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  [key: string]: any // Allow additional properties
}

export interface LineSeries {
  name: string
  dataKey: string
  stroke: string
  data?: DataPoint[]
  hide?: boolean
  strokeOpacity?: number
  activeDot?: unknown
}

interface GridConfig {
  vertical?: boolean
  horizontal?: boolean
}

export interface LineChartProps {
  data: DataPoint[]
  series?: LineSeries[]
  width?: number | string
  height?: number | string
  tooltipFormatter?: (
    dataPoint: DataPoint,
    series?: LineSeries[],
  ) => React.ReactNode
  showGrid?: boolean | GridConfig
  showDots?: boolean
  displayMode?: string
  interval?: AxisInterval
  isPricesLoading?: boolean
  isFetching?: boolean
  useFastAnimation?: boolean
  onAnimationEnd?: () => void
  curveType?: CurveType
  xAxisFormatter?: (value: number | string | null) => string
  xAxisTicks?: number[]
  yAxisTicks?: number[]
  customYAxisTick?: React.ReactElement
  showXAxisBorder?: boolean
  xAxisBorderPosition?: number
  yAxisDomain?: [string | number, string | number]
  yAxisTickCount?: number
  connectNulls?: boolean
  freezeDataDelay?: number
  freezeYAxisDomain?: boolean
  fadeStart?: boolean
  showYAxis?: boolean
}

// Constants types
export interface ChartMargins {
  readonly left: number
  readonly right: number
  readonly bottom: number
  readonly top: number
}

export interface ChartOffsets {
  getHorizontalOffsets: (height: number) => number[]
}

export interface ChartStyles {
  readonly fontSize: {
    readonly axis: number
    readonly tooltip: number
  }
  readonly padding: {
    readonly xAxis: {
      readonly left: number
      readonly right: number
    }
    readonly yAxis: {
      readonly top: number
      readonly bottom: number
    }
  }
  readonly animation: {
    readonly duration: {
      readonly drawLine: number
      readonly morphLine: number
      readonly tooltip: number
    }
  }
}

// Component prop types
export interface ChartContainerProps {
  children: React.ReactNode
  className?: string
}

export interface ChartSkeletonContainerProps {
  children: React.ReactNode
  isFetching: boolean
}

export interface SkeletonBarProps {
  y: number
  className?: string
}

// Base props shared by all axis components
interface BaseAxisProps {
  x?: number
  y?: number
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  payload?: any
  className?: string
}

// Props for basic axis text rendering
export interface AxisTextProps extends BaseAxisProps {
  textAnchor?: 'start' | 'middle' | 'end'
  xOffset?: number
  dy?: number
}

// Props specific to X-axis ticks
export interface XAxisTickProps extends BaseAxisProps {
  isTransitioning?: boolean
  formatter?: (value: number | string | null) => string
}

// Props specific to Y-axis ticks
export interface YAxisTickProps extends BaseAxisProps {
  displayMode: string
  digits: number
}

export interface CustomizedDotProps {
  cx?: number
  cy?: number
  index?: number
  data: DataPoint[]
}

export interface TooltipWithFallbackProps {
  active?: boolean
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  payload?: any[]
  label?: string
  tooltipFormatter?: (
    dataPoint: DataPoint,
    series?: LineSeries[],
  ) => React.ReactNode
  series?: LineSeries[]
}

export interface SkeletonProps {
  isFetching: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
}
